import { DuplicateIcon } from '@eleveo/icons-react';
import { Button, Col, FormGroup, Row, Tooltip } from '@zoom/components';
import { isEmpty } from 'ramda';
import React, { useMemo } from 'react';
import { Field, FormProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { ACDStatusSelectField, composeAcdStatusId, getAcdStatusIds } from './ACDStatusSelectField';
import { ScheduledActivitySelectField } from './ScheduledActivitySelectField';
import { CANCEL_SETTING_BUTTON_TAG, SAVE_SETTING_BUTTON_TAG, getFieldsInputTag } from './constants';
import { Legend } from '../../components/Form/Legend';
import { finalFormInputHelper } from '../../components/Inputs/RenderInput';
import { SidebarBody, SidebarFormWrapper, SidebarHeader } from '../../components/layout/Sidebar';
import {
  AdherenceEventType,
  AdherenceReasonCode,
  AdherenceSetting,
  ScheduleElement,
  useGetUsedAdherenceAcdStatusesQuery,
} from '../../graphql/components';
import { nameOf } from '../../utils/helpers';
import { max255chars, required } from '../../utils/validations';
import { composeValidators } from '../../utils/validations/composeValidators';
import { requiredAtLeastOneOfFields } from '../../utils/validations/requiredAtLeastOneOfFields';

type AdherenceSettingsFormSidebarProps = {
  onSubmit: FormProps<AdherenceSettingFormValues>['onSubmit'];
  onCancel: () => void;
  setting: AdherenceSetting | null;
  scheduleElements: ScheduleElement[];
  eventTypes: AdherenceEventType[];
  reasonCodes: AdherenceReasonCode[];
};

export type AdherenceSettingFormValues = ReturnType<typeof formatValues>;

/**
 * Format `AdherenceSetting` to fit `Form` input fields
 */
export const formatValues = (setting: AdherenceSetting | null) => {
  if (setting === null) {
    return {
      name: '',
      historyScheduleActivityIds: [],
      realTimeScheduleActivityIds: [],
      acdStatuses: [],
    };
  }

  const { name, historyScheduleActivityIds, realTimeScheduleActivityIds } = setting;

  const acdStatuses = setting.acdStatuses.map(composeAcdStatusId);

  return {
    acdStatuses,
    name,
    historyScheduleActivityIds,
    realTimeScheduleActivityIds,
  };
};

/**
 * Parse `Form` values to `AdherenceSetting` format suitable for GraphQL mutation
 */
export const parseValues = (values: AdherenceSettingFormValues) => {
  const { acdStatuses: acdStatusOptions, name, historyScheduleActivityIds, realTimeScheduleActivityIds } = values;

  const acdStatuses = acdStatusOptions.map(getAcdStatusIds);

  return {
    name,
    historyScheduleActivityIds,
    realTimeScheduleActivityIds,
    acdStatuses,
  };
};

export const AdherenceSettingsFormSidebar: React.FC<AdherenceSettingsFormSidebarProps> = ({
  onCancel: handleCancel,
  onSubmit: handleSubmit,
  setting,
  scheduleElements,
  eventTypes,
  reasonCodes,
}) => {
  const { t } = useTranslation();
  const { data: usedAcdStatusesData, loading: loadingUsedAcdStatuses } = useGetUsedAdherenceAcdStatusesQuery({
    fetchPolicy: 'no-cache',
  });

  const initialValues = useMemo(() => formatValues(setting), [setting]);

  return (
    <SidebarFormWrapper onSubmit={handleSubmit} initialValues={initialValues}>
      {({ pristine, invalid, values, form }) => (
        <>
          <SidebarHeader onClose={handleCancel}>
            <Button
              data-test-id={SAVE_SETTING_BUTTON_TAG}
              // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
              disabled={pristine || invalid}
              kind="secondary"
              type="submit"
            >
              {t('layout.action.save')}
            </Button>
            <Button
              data-test-id={CANCEL_SETTING_BUTTON_TAG}
              className="button-icon-left mr-auto ml-2"
              color="info"
              icon={{ color: 'red', kind: 'cross', size: 10 }}
              onClick={handleCancel}
            >
              {t('layout.action.cancel')}
            </Button>
          </SidebarHeader>
          <SidebarBody>
            <div className="p-4">
              <fieldset>
                <Legend>{setting ? t('scheduleAdherence.editStatus') : t('scheduleAdherence.addStatus')}</Legend>
                <FormGroup>
                  <Field
                    name={nameOf<typeof values>('name')}
                    validate={composeValidators(required, max255chars)}
                    render={finalFormInputHelper({
                      label: t('scheduleAdherence.eleveoStatus'),
                      dataTestId: getFieldsInputTag(nameOf<typeof values>('name')),
                      type: 'text',
                    })}
                  />
                </FormGroup>
                <FormGroup>
                  <ACDStatusSelectField
                    className="w-100"
                    dataTestId={getFieldsInputTag(nameOf<typeof values>('acdStatuses'))}
                    disabled={loadingUsedAcdStatuses}
                    name={nameOf<typeof values>('acdStatuses')}
                    eventTypes={eventTypes}
                    reasonCodes={reasonCodes}
                    usedAcdStatuses={usedAcdStatusesData?.adherenceAcdStatuses ?? []}
                    validate={required}
                  />
                </FormGroup>
                <Row>
                  <Col sm="auto" className="flex-fill">
                    <ScheduledActivitySelectField
                      dataTestId={getFieldsInputTag(nameOf<typeof values>('historyScheduleActivityIds'))}
                      name={nameOf<typeof values>('historyScheduleActivityIds')}
                      scheduleElements={scheduleElements}
                      validate={requiredAtLeastOneOfFields([['realTimeScheduleActivityIds']])}
                    />
                  </Col>
                  <Col sm="auto">
                    <Tooltip message={t('scheduleAdherence.copySelectionFromRTA')} placement="bottom">
                      <Button
                        data-test-id={getFieldsInputTag('button-copy-from-rta')}
                        // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
                        disabled={isEmpty(values.realTimeScheduleActivityIds)}
                        kind="icon-bordered"
                        onClick={() => form.change('historyScheduleActivityIds', values.realTimeScheduleActivityIds)}
                      >
                        <DuplicateIcon />
                      </Button>
                    </Tooltip>
                  </Col>
                </Row>
                <Row>
                  <Col sm="auto" className="flex-fill">
                    <ScheduledActivitySelectField
                      dataTestId={getFieldsInputTag(nameOf<typeof values>('realTimeScheduleActivityIds'))}
                      label="scheduleAdherence.realTimeAdherence.rta"
                      name={nameOf<typeof values>('realTimeScheduleActivityIds')}
                      scheduleElements={scheduleElements}
                      validate={requiredAtLeastOneOfFields([['historyScheduleActivityIds']])}
                    />
                  </Col>
                  <Col sm="auto">
                    <Tooltip message={t('scheduleAdherence.copySelectionFromHistory')} placement="bottom">
                      <Button
                        data-test-id={getFieldsInputTag('button-copy-from-history')}
                        // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
                        disabled={isEmpty(values.historyScheduleActivityIds)}
                        kind="icon-bordered"
                        onClick={() => form.change('realTimeScheduleActivityIds', values.historyScheduleActivityIds)}
                      >
                        <DuplicateIcon />
                      </Button>
                    </Tooltip>
                  </Col>
                </Row>
              </fieldset>
            </div>
          </SidebarBody>
        </>
      )}
    </SidebarFormWrapper>
  );
};
